import { css } from 'styled-components';

/**
 * The outline declaration shared by `focusRing` and `focusRingOn` — kept in
 * one place so a future fix to the ring itself doesn't need to touch both.
 */
const focusOutline = css`
  outline: 2px solid ${p => p.theme.colors.brown};
  outline-offset: 2px;
`;

/**
 * Visible keyboard focus indicator for interactive `src/ui` primitives.
 *
 * Never pair this with `outline: none` on the base state — focus rings are
 * an accessibility requirement (WCAG 2.4.7), and `:focus-visible` already
 * keeps the ring from showing on mouse/touch interaction.
 */
export const focusRing = css`
  &:focus-visible {
    ${focusOutline}
  }
`;

/**
 * Same ring as `focusRing`, anchored to an arbitrary selector instead of
 * `&:focus-visible`. For components whose focusable element is visually
 * hidden (e.g. a native radio/checkbox input clipped off-screen for a11y),
 * so the ring has to render on the sibling that visually represents it.
 *
 * Pass the full selector, `&` included, e.g.
 * `focusRingOn(css\`${HiddenInput}:focus-visible + &\`)`.
 */
export const focusRingOn = (selector: ReturnType<typeof css>) => css`
  ${selector} {
    ${focusOutline}
  }
`;

/**
 * Standard "visually hidden" clip pattern: content stays in the
 * accessibility tree (readable by screen readers, focusable if interactive)
 * but is removed from the visual layout. Shared by `VisuallyHidden` and
 * anywhere else that needs the same pattern on an element that isn't a
 * plain wrapper span (e.g. `SelectableCard`'s native radio input, or its
 * group's visually-hidden `Legend`).
 */
export const visuallyHiddenCss = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * Wraps a block of CSS (e.g. `animation: none;`) so it only applies when
 * the user has requested reduced motion. Pair with normal, always-on
 * animation/transition CSS elsewhere in the same styled component.
 *
 * `window.matchMedia` is undefined in jsdom 16 (the environment
 * `react-scripts test` runs under), so this must stay a plain CSS
 * `@media` query — never gated behind a `matchMedia` call in JS.
 */
export const reducedMotion = (styles: ReturnType<typeof css>) => css`
  @media (prefers-reduced-motion: reduce) {
    ${styles}
  }
`;

/**
 * WCAG 2.1 relative luminance and contrast ratio, for asserting token pairs in
 * tests. Implements SC 1.4.3 exactly: sRGB channels linearised, then
 * `(Lmax + 0.05) / (Lmin + 0.05)`.
 *
 * Thresholds, for reference at the call site: normal text needs 4.5, large
 * text 3 (>=24px, or >=18.66px when bold — note 15px bold is NOT large), and
 * non-text UI components, borders that convey state and focus indicators need
 * 3. Takes opaque 6-digit hex only; composite any alpha over its backdrop
 * before calling.
 */
export function contrastRatio(a: string, b: string): number {
  const luminance = (hex: string) => {
    const n = parseInt(hex.replace('#', ''), 16);
    const channel = (c: number) => {
      const v = c / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };

    return (
      0.2126 * channel((n >> 16) & 255) +
      0.7152 * channel((n >> 8) & 255) +
      0.0722 * channel(n & 255)
    );
  };

  const [x, y] = [luminance(a), luminance(b)];

  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
